import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { useMutation } from 'react-query';
import {
  Field,
  Icon,
  Select,
  Text,
  Textarea,
  TextInput,
  tokens,
  useNotify,
} from '@hotjar/hotsauce';

import type { CountryCode } from '@hotjar/country-lib';
import { COUNTRIES } from '@hotjar/country-lib';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { SalesTaxRepository } from '@hotjar/webapp/billing/tax';
import { getPreviewedAuthenticatedPaymentMethod } from '@hotjar/webapp/billing/authenticate';
import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { OwnedAccountDTO } from '@hotjar/account';
import type { UpdateCompanyDetails } from '../api';
import { updateCompanyDetails } from '../api';
import { MODAL_NAME } from '../constants';

export type FormInputs = {
  company: string;
  address?: string;
  countryCode: CountryCode;
  vatNumber?: string;
};

const S = {
  Form: styled.form`
    ${tokens.flexContainer('form')};
  `,
  Field: styled(Field)`
    width: 100%;

    label {
      text-transform: unset;
    }
  `,
  TaxInput: styled(TextInput)<{ iso: FormInputs['countryCode'] }>`
    ${({ iso }) =>
      iso &&
      `
      &:before {
        content: '${iso}';
        margin-left: ${tokens.spacer.x2};
        font: ${tokens.text('subtitle1')};
      }
    `}
  `,
};

interface Props {
  onSuccess(): void;
  onLoadingChange(state: boolean): void;
  formId: string;
  defaultValues: FormInputs;
  account: OwnedAccountDTO;
}

export function LegacyCompanyDetailsForm({
  onSuccess,
  onLoadingChange,
  formId,
  defaultValues,
  account,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('billing:company-details.legacy.form');
  const { showToast } = useNotify();
  const { click: trackClick, companyDetailsFormSubmitted } = useTrack();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<FormInputs>({ defaultValues });

  const selectedCountry = watch('countryCode');
  const salesTax = SalesTaxRepository.byCountryCode(selectedCountry);

  function trackFormSubmit({
    success,
    failureReason,
  }: {
    success: boolean;
    failureReason?: string;
  }) {
    companyDetailsFormSubmitted({
      success,
      taxFieldPresent: !!salesTax?.type,
      taxFieldFilled: !!watch('vatNumber'),
      countrySelectChanged: defaultValues.countryCode !== watch('countryCode'),
      failureReason,
    });
  }

  const companyDetails = useMutation(
    async (data: UpdateCompanyDetails) => {
      trackClick('Continue', MODAL_NAME, {});
      onLoadingChange(true);

      const { vatNumber, countryCode } = data;
      const { numberPrefix } = salesTax || {};
      const updatedVatNumber = vatNumber ? `${numberPrefix}${vatNumber}` : undefined;

      const shouldAuthenticate = !(
        countryCode === defaultValues.countryCode && vatNumber === defaultValues.vatNumber
      );
      const accountOverrides = { vat_number: updatedVatNumber, location: countryCode };

      const { nonce } = await getPreviewedAuthenticatedPaymentMethod(
        account,
        shouldAuthenticate,
        accountOverrides,
        PAYMENT_ORIGINS.vatCountryChange,
      );

      return updateCompanyDetails({
        ...data,
        vatNumber: updatedVatNumber,
        paymentMethodNonce: nonce ?? undefined,
      });
    },
    {
      onSuccess: () => {
        trackFormSubmit({ success: true });
        onSuccess();
      },
      onError: ({ message }: Error) => {
        const type = salesTax?.type.label;
        let toastText = 'error.unhandled-request';
        let failureReason;

        if (message === 'Invalid VAT number.') {
          setError('vatNumber', { message: t('validation.tax-invalid', { type }) });
          toastText = 'error.invalid-tax';
          failureReason = 'tax_invalid';
        } else if (message === 'Vat validation failure.') {
          setError('vatNumber', { message: t('validation.tax-failure', { type }) });
          toastText = 'error.failure-tax';
          failureReason = 'tax_failure';
        } else if (message === 'Account is past due.') {
          toastText = 'error.account-past-due';
          failureReason = 'account_past_due';
        }

        trackFormSubmit({ success: false, failureReason });
        showToast({
          text: t(toastText, { type }),
          kind: 'danger',
        });
      },
      onSettled: () => onLoadingChange(false),
    },
  );

  const taxNotification = (() => {
    if (selectedCountry) {
      if (salesTax) {
        const { rate, type } = salesTax;
        const translation = salesTax.reverseChargeInapplicable
          ? 'notification.tax-reverse-charge-inapplicable'
          : 'notification.tax-reverse-charge-applicable';

        return t(translation, { rate, type: type.label });
      }
    }
    return null;
  })();

  return (
    <S.Form
      onSubmit={handleSubmit((formData) => {
        companyDetails.mutate({ ...formData, account });
      })}
      id={formId}
    >
      <S.Field labelText={t('label.company')} inputId="company">
        <TextInput
          id="company"
          name="company"
          placeholder={t('placeholder.company')}
          ref={register}
        />
      </S.Field>

      <S.Field labelText={t('label.address')} inputId="address">
        <Textarea id="address" name="address" ref={register} />
      </S.Field>

      <S.Field
        labelText={t('label.country')}
        inputId="countryCode"
        isRequired
        isInvalid
        invalidText={errors.countryCode?.message}
      >
        <Select
          id="countryCode"
          name="countryCode"
          ref={register({ required: t<string>('validation.country') })}
          isRequired
          isInvalid={!!errors.countryCode}
        >
          <option value="" disabled>
            {t('placeholder.country')}
          </option>
          {COUNTRIES.map(({ label, value }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </Select>
      </S.Field>

      {salesTax?.type && (
        <S.Field
          labelText={t(`label.${salesTax.type?.label?.toLowerCase()}`)}
          inputId="vatNumber"
          isInvalid
          invalidText={errors.vatNumber?.message}
        >
          <S.TaxInput
            id="vatNumber"
            name="vatNumber"
            placeholder={t('placeholder.tax', { taxType: salesTax.type?.label })}
            iso={selectedCountry}
            isInvalid={!!errors.vatNumber}
            ref={register}
          />
        </S.Field>
      )}

      {taxNotification && (
        <Text as="p" textType="body2" textColor="text_light">
          <Icon icon="info" inline /> {taxNotification}
        </Text>
      )}
    </S.Form>
  );
}
